import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { adminRastreioExternoGet, adminRastreioExternoSave, adminRastreioExternoTestar, adminRastreioExternoLimite } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const RastreioExternoSection = () => {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [chave, setChave] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [teste, setTeste] = useState(null);
  const [limite, setLimite] = useState('');
  const [salvandoLimite, setSalvandoLimite] = useState(false);

  useEffect(() => { adminRastreioExternoGet().then((d) => { setCfg(d); setLimite(String(d.limite_cliente_mes ?? 10)); }).catch(() => setCfg({ configurado: false })); }, []);

  const salvar = async (valor) => {
    setSalvando(true); setTeste(null);
    try {
      setCfg(await adminRastreioExternoSave(valor));
      setChave('');
      toast({ title: valor ? 'Chave salva' : 'Chave removida' });
    } catch (err) {
      toast({ title: 'Não foi possível salvar', description: err?.response?.data?.detail || 'Tente novamente.' });
    } finally { setSalvando(false); }
  };
  const salvarLimite = async () => {
    const n = parseInt(limite, 10);
    if (Number.isNaN(n) || n < 0) { toast({ title: 'Informe um número de 0 a 1000' }); return; }
    setSalvandoLimite(true);
    try { setCfg(await adminRastreioExternoLimite(n)); toast({ title: 'Limite salvo', description: `Cada cliente pode consultar ${n} códigos de outros contratos por mês.` }); }
    catch (err) { toast({ title: 'Não foi possível salvar', description: err?.response?.data?.detail || 'Tente novamente.' }); }
    finally { setSalvandoLimite(false); }
  };
  const testar = async () => {
    setTestando(true); setTeste(null);
    try { setTeste(await adminRastreioExternoTestar()); } catch { setTeste({ ok: false, mensagem: 'Não foi possível testar agora.' }); } finally { setTestando(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-100 p-6 max-w-2xl">
      <h2 className="text-lg font-semibold text-slate-900">Rastreio de qualquer código</h2>
      <p className="text-sm text-slate-500 mb-4">
        Por padrão, a tela de Rastreio só mostra o histórico de envios feitos pelo contrato da In&apos;Nova nos Correios.
        Para consultar também <strong>códigos de outras empresas e contratos</strong> (compras que seus clientes recebem), ligue um serviço de rastreio.
      </p>
      {cfg && (
        <p className={`flex items-center gap-2 text-sm font-medium ${cfg.configurado ? 'text-emerald-600' : 'text-amber-600'}`}>
          {cfg.configurado ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {cfg.configurado ? `Ativo — chave terminada em ${cfg.chave_final}. Consultas externas neste mês: ${cfg.usos_mes}.` : 'Desligado — códigos de outros contratos só abrem no site dos Correios.'}
        </p>
      )}

      <div className="mt-5">
        <Label htmlFor="chave-rastreio">Chave de API do Seu Rastreio</Label>
        <div className="flex gap-2 mt-1.5">
          <Input id="chave-rastreio" type="password" value={chave} onChange={(e) => setChave(e.target.value)} placeholder="sr_live_..." autoComplete="off" className="h-10" />
          <Button onClick={() => salvar(chave)} disabled={salvando || !chave.trim()} className="inn-btn-primary h-10 rounded-full px-5">
            {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
          </Button>
        </div>
        {cfg?.configurado && (
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <Button variant="outline" onClick={testar} disabled={testando} className="h-9">{testando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testando...</> : 'Testar chave'}</Button>
            <button type="button" onClick={() => salvar('')} className="text-sm text-red-600 hover:underline">Remover chave</button>
          </div>
        )}
        {teste && <p className={`mt-3 text-sm rounded-lg p-3 ${teste.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>{teste.mensagem}</p>}
      </div>

      <div className="mt-6 border-t border-slate-100 pt-5">
        <Label htmlFor="limite-rastreio">Limite por cliente (códigos de outros contratos por mês)</Label>
        <p className="text-xs text-slate-500 mt-1">Cada cliente gasta da sua cota mensal do serviço. Repetir o mesmo código não conta, e você (administrador) não tem limite. Use 0 para bloquear a consulta externa para os clientes.</p>
        <div className="flex gap-2 mt-2">
          <Input id="limite-rastreio" type="number" min="0" max="1000" value={limite} onChange={(e) => setLimite(e.target.value)} className="h-10 w-32" />
          <Button variant="outline" onClick={salvarLimite} disabled={salvandoLimite} className="h-10">{salvandoLimite ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar limite'}</Button>
        </div>
      </div>

      <div className="mt-6 text-sm text-slate-600 bg-slate-50 rounded-xl p-4 space-y-2">
        <p className="font-semibold text-slate-800">Como ligar</p>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Acesse <a href="https://seurastreio.com.br" target="_blank" rel="noreferrer" className="text-[#2A5BC7] underline">seurastreio.com.br</a> e crie a conta (há 7 dias grátis, sem cartão).</li>
          <li>Escolha um plano. O <strong>Essencial</strong> (R$ 37/mês, 200 rastreios) já inclui a API; veja na página deles se o seu plano mostra o histórico completo ou só o último evento.</li>
          <li>No painel deles, gere a <strong>chave de API</strong> (começa com <code>sr_live_</code>) e cole acima.</li>
          <li>Clique em <strong>Testar chave</strong>. Pronto: qualquer código dos Correios passa a abrir na tela de Rastreio.</li>
        </ol>
        <p className="text-xs text-slate-500">Os códigos consultados são enviados a esse serviço. O sistema guarda o resultado por 10 minutos para não gastar sua cota, e limita as consultas por cliente.</p>
      </div>
    </section>
  );
};

export default RastreioExternoSection;
